import React, { useEffect, useRef, useMemo } from 'react';
import Chart from 'chart.js/auto';

const ProjectSizeChart = ({ alumniData = [] }) => {
  const chartRef = useRef(null);
  const chartInstance = useRef(null);
  
  // Process alumni data to get project size distribution
  const projectSizeData = useMemo(() => {
    // Define project size categories
    const categories = ['<$100K', '$100K-$500K', '$500K-$1M', '$1M-$5M', '>$5M'];
    const projectSizes = Array(categories.length).fill(0);
    
    // Count alumni by potential project size (based on role and company size)
    alumniData.forEach(alumnus => {
      // Determine project size category based on role and company
      let sizeIndex = 0;
      const title = alumnus.currentTitle || '';
      const companySize = alumnus.companySize || '';
      
      // Executive roles typically lead to larger projects
      const isExecutive = ['CTO', 'CEO', 'CFO', 'CIO', 'COO', 'VP', 'Director', 'President', 'Chief'].some(
        role => title.includes(role)
      );
      
      // Determine size index based on role and company size
      if (isExecutive) {
        if (companySize.includes('Large') || companySize.includes('Enterprise')) {
          sizeIndex = 4; // >$5M
        } else if (companySize.includes('Medium')) {
          sizeIndex = 3; // $1M-$5M
        } else {
          sizeIndex = 2; // $500K-$1M
        }
      } else if (title.includes('Manager') || title.includes('Lead')) {
        if (companySize.includes('Large') || companySize.includes('Enterprise')) {
          sizeIndex = 3; // $1M-$5M
        } else if (companySize.includes('Medium')) {
          sizeIndex = 2; // $500K-$1M
        } else {
          sizeIndex = 1; // $100K-$500K
        }
      } else {
        if (companySize.includes('Large') || companySize.includes('Enterprise')) {
          sizeIndex = 2; // $500K-$1M
        } else if (companySize.includes('Medium')) {
          sizeIndex = 1; // $100K-$500K
        } else {
          sizeIndex = 0; // <$100K
        }
      }
      
      // Increment the appropriate size category
      projectSizes[sizeIndex]++;
    });
    
    // If we don't have any data, use some defaults
    if (projectSizes.every(size => size === 0)) {
      // Generate some random data based on the number of alumni
      const totalAlumni = alumniData.length || 100;
      projectSizes[0] = Math.floor(totalAlumni * 0.15); // <$100K: 15%
      projectSizes[1] = Math.floor(totalAlumni * 0.25); // $100K-$500K: 25%
      projectSizes[2] = Math.floor(totalAlumni * 0.30); // $500K-$1M: 30%
      projectSizes[3] = Math.floor(totalAlumni * 0.20); // $1M-$5M: 20%
      projectSizes[4] = Math.floor(totalAlumni * 0.10); // >$5M: 10%
    }
    
    return {
      labels: categories,
      data: projectSizes
    };
  }, [alumniData]);

  useEffect(() => {
    // Destroy existing chart if it exists
    if (chartInstance.current) {
      chartInstance.current.destroy();
    }

    // Create chart with real or generated data
    const data = {
      labels: projectSizeData.labels,
      datasets: [
        {
          label: 'Project Size Distribution',
          data: projectSizeData.data,
          backgroundColor: 'rgba(75, 192, 192, 0.6)',
          borderColor: 'rgba(75, 192, 192, 1)',
          borderWidth: 1
        }
      ]
    };

    // Create new chart
    const ctx = chartRef.current.getContext('2d');
    chartInstance.current = new Chart(ctx, {
      type: 'bar',
      data: data,
      options: {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          y: {
            beginAtZero: true,
            title: {
              display: true,
              text: 'Number of Projects'
            }
          },
          x: {
            title: {
              display: true,
              text: 'Project Size'
            }
          }
        },
        plugins: {
          legend: {
            display: false
          }
        }
      }
    });

    // Cleanup function
    return () => {
      if (chartInstance.current) {
        chartInstance.current.destroy();
      }
    };
  }, [projectSizeData]);

  return (
    <div style={{ height: '300px' }}>
      <canvas ref={chartRef}></canvas>
    </div>
  );
};

export default ProjectSizeChart;
